import React, { useState, useMemo, useEffect } from 'react';
import { Text, Box, Heading, Select, VStack, IconButton, Collapse } from '@chakra-ui/react';
import { StemiBeatLoader } from '../../../../../components/Loader';
import useFileUploadsFromActiveLicenses from '../../../hooks/useFileUploadsFromActiveLicenses';
import { AuthContext, Role } from '../../../../../context/AuthProvider';
import { useContext } from 'react';
import CloudinaryUploadWidget from '../../../../app/components/CloudinaryUploadWidget';
import { CHALLENGE_VIRTUAL_STUDY_GROUP_ID } from './constants';
import { useIsMobile } from '../../../../../hooks/isMobile';
import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import { currentSchoolYear } from '../../../../../helpers/schoolYear';
import ReactSelect from 'react-select';

interface FileUpload {
    id: string;
    student_name: string;
    student_email: string;
    student_username: string;
    filename: string;
    file_url: string;
    program_name: string | null;
    program_id: string | null;
    school_name: string | null;
    actual_student_school_name: string | null;
    school_id: string | null;
    slug: string;
    class_id: string | null;
    student_id: string;
}

interface AdminFileUploadsListProps {
    slug: string;
}

const AdminFileUploadsList: React.FC<AdminFileUploadsListProps> = ({ slug }) => {
    const user = useContext(AuthContext);
    const [selectedSchoolName, setSelectedSchoolName] = useState<string>('');
    const [selectedSchoolYear, setSelectedSchoolYear] = useState<string>(currentSchoolYear());
    const [selectedStudentId, setSelectedStudentId] = useState<string | null>(null);
    const { fileUploadsFromActiveLicenses, availableSchoolYears, isLoading } = useFileUploadsFromActiveLicenses(slug, selectedSchoolYear);
    const isMobile = useIsMobile();
    const [isOpen, setIsOpen] = useState<boolean>(false);

    useEffect(() => {
        if (availableSchoolYears.length > 0 && !availableSchoolYears.includes(selectedSchoolYear)) {
            setSelectedSchoolYear(availableSchoolYears[0]!);
            setSelectedSchoolName('');
            setSelectedStudentId(null);
        }
    }, [availableSchoolYears]);

    if (!user?.roles.includes(Role.staff)) {
        return null;
    }

    const uploadMatchesSchool = (upload: FileUpload) => {
        if (!selectedSchoolName) return true;
        if (upload.class_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
            return upload.actual_student_school_name === selectedSchoolName;
        }
        return upload.school_name === selectedSchoolName;
    };

    const schools = useMemo(() => {
        const names = new Set<string>();
        fileUploadsFromActiveLicenses.forEach((upload: FileUpload) => {
            if (upload.class_id === CHALLENGE_VIRTUAL_STUDY_GROUP_ID) {
                if (upload.actual_student_school_name) {
                    names.add(upload.actual_student_school_name);
                }
            } else if (upload.school_name) {
                names.add(upload.school_name);
            }
        });
        return Array.from(names).sort((a, b) => a.localeCompare(b));
    }, [fileUploadsFromActiveLicenses]);

    const studentOptions = useMemo(() => {
        const studentMap = new Map<string, { value: string; label: string }>();
        fileUploadsFromActiveLicenses.filter(uploadMatchesSchool).forEach((upload: FileUpload) => {
            studentMap.set(upload.student_id, {
                value: upload.student_id,
                label: upload.student_name || upload.student_username || upload.student_id
            });
        });
        return Array.from(studentMap.values()).sort((a, b) => a.label.localeCompare(b.label));
    }, [fileUploadsFromActiveLicenses, selectedSchoolName]);

    const filteredUploads = useMemo(() => {
        let filtered = fileUploadsFromActiveLicenses.filter(uploadMatchesSchool);
        if (selectedStudentId) {
            filtered = filtered.filter((upload: FileUpload) => upload.student_id === selectedStudentId);
        }
        return filtered;
    }, [fileUploadsFromActiveLicenses, selectedSchoolName, selectedStudentId]);

    return (
        <Box mt={8} p={4} borderWidth="1px" borderRadius="lg">
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
                <Heading size="md">Admin View: File Uploads from all students</Heading>
                <IconButton
                    aria-label={isOpen ? 'Collapse' : 'Expand'}
                    icon={isOpen ? <ChevronUpIcon /> : <ChevronDownIcon />}
                    onClick={() => setIsOpen(!isOpen)}
                    variant="ghost"
                />
            </Box>

            <Collapse in={isOpen}>
                <VStack spacing={4} align="stretch">
                    <Box display="flex" gap={4} flexWrap="wrap">
                        {availableSchoolYears.length > 1 && (
                            <Box flex="1" minW="200px">
                                <Text mb={2}>School Year:</Text>
                                <Select
                                    value={selectedSchoolYear}
                                    onChange={(e) => {
                                        setSelectedSchoolYear(e.target.value);
                                        setSelectedSchoolName('');
                                        setSelectedStudentId(null);
                                    }}
                                >
                                    {availableSchoolYears.map((year) => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </Select>
                            </Box>
                        )}
                        <Box flex="1" minW="200px">
                            <Text mb={2}>Filter by School:</Text>
                            <Select
                                placeholder="All Schools"
                                value={selectedSchoolName}
                                onChange={(e) => {
                                    setSelectedSchoolName(e.target.value);
                                    setSelectedStudentId(null);
                                }}
                            >
                                {schools.map((name) => (
                                    <option key={name} value={name}>{name}</option>
                                ))}
                            </Select>
                        </Box>
                    </Box>

                    <Box>
                        <Text mb={2}>Filter by Student:</Text>
                        <ReactSelect
                            styles={{
                                container: (provided: any) => ({
                                    ...provided,
                                    minWidth: '350px',
                                    textAlign: 'left',
                                }),
                            }}
                            isClearable
                            placeholder="Select student"
                            noOptionsMessage={() => 'No students'}
                            options={studentOptions}
                            value={studentOptions.find((o) => o.value === selectedStudentId) ?? null}
                            onChange={(value: { value: string; label: string } | null) => {
                                setSelectedStudentId(value?.value || null);
                            }}
                            menuPlacement="auto"
                            menuPortalTarget={document.body}
                        />
                    </Box>

                    <Box
                        maxH="700px"
                        overflowY="auto"
                        css={{
                            '&::-webkit-scrollbar': {
                                width: '4px',
                            },
                            '&::-webkit-scrollbar-track': {
                                width: '6px',
                                background: 'gray.100',
                            },
                            '&::-webkit-scrollbar-thumb': {
                                background: 'gray.400',
                                borderRadius: '24px',
                            },
                        }}
                    >
                        {isLoading ? (
                            <StemiBeatLoader />
                        ) : filteredUploads.length === 0 ? (
                            <Text>No file uploads found{selectedSchoolName ? ' for selected school' : ''}</Text>
                        ) : (
                            filteredUploads.map((file: FileUpload, index: number) => (
                                <Box
                                    key={`${file.id}-${index}`}
                                    p={4}
                                    mb={4}
                                    borderWidth="1px"
                                    borderRadius="md"
                                    bg="gray.50"
                                >
                                    <div style={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', flexDirection: isMobile ? 'column' : 'row', margin: '4px', justifyContent: isMobile ? 'flex-start' : 'space-between' }}>
                                        <span style={isMobile ? {} : { width: '260px', maxWidth: '260px' }}>
                                            <Text fontWeight="medium">{file.student_name}</Text>
                                            {!file.student_name && (
                                                <Text fontSize="sm" fontWeight="medium">username: {file.student_username}</Text>
                                            )}
                                            {file.program_name && (
                                                <Text fontSize="sm">Program: {file.program_name}</Text>
                                            )}
                                            {file.actual_student_school_name && (
                                                <Text fontSize="sm">School: {file.actual_student_school_name}</Text>
                                            )}
                                            <Text fontSize="sm">slug: {file.slug}</Text>
                                        </span>
                                        <CloudinaryUploadWidget
                                            isViewOnly={true}
                                            isImageOnly={false}
                                            initialUrl={file.file_url}
                                            initialFilename={file.filename}
                                            onImageUpload={() => { }}
                                        />
                                    </div>
                                </Box>
                            ))
                        )}
                    </Box>
                </VStack>
            </Collapse>
        </Box>
    );
};

export default AdminFileUploadsList;
